import {
  getBrowserFeature,
  type VirtualKeyboardGeometryChangeEvent,
} from '../../utils';
import { listenForViewportChange } from './listenForViewportChange';
import { type Bounds, getWindowBounds } from './getWindowBounds';

const hookInitValues = getWindowBounds();

let bounds: Bounds = hookInitValues;
const listeners = new Set<() => void>();
let globalListenersAttached = false;
let virtualKeyboardHeight = 0;
let removeGlobalListeners: (() => void) | undefined;

const emitChange = (): void => {
  listeners.forEach((listener) => listener());
};

const syncBounds = (): void => {
  const windowBounds = getWindowBounds();
  const next: Bounds = {
    width: windowBounds.width,
    height: windowBounds.height - virtualKeyboardHeight,
  };

  if (next.width === bounds.width && next.height === bounds.height) {
    return;
  }

  bounds = next;
  emitChange();
};

const updateBoundsOnGeometryChange = (
  event: VirtualKeyboardGeometryChangeEvent,
): void => {
  virtualKeyboardHeight = event.target.boundingRect.height;
  syncBounds();
};

const attachGlobalListeners = (): void => {
  if (globalListenersAttached || typeof window === 'undefined') {
    return;
  }

  globalListenersAttached = true;

  const { virtualKeyboard } = getBrowserFeature();
  const { addListener, removeListener } = listenForViewportChange(syncBounds);

  syncBounds();
  addListener();
  virtualKeyboard?.addEventListener(
    'geometrychange',
    updateBoundsOnGeometryChange,
  );

  removeGlobalListeners = (): void => {
    removeListener();
    virtualKeyboard?.removeEventListener(
      'geometrychange',
      updateBoundsOnGeometryChange,
    );
  };
};

export const subscribeToBounds = (onStoreChange: () => void): (() => void) => {
  attachGlobalListeners();
  listeners.add(onStoreChange);

  return () => {
    listeners.delete(onStoreChange);

    // Detach the global listeners once nothing is observing bounds. The next
    // subscriber re-attaches and re-syncs, so no resize is missed across the gap.
    if (listeners.size === 0) {
      removeGlobalListeners?.();
      removeGlobalListeners = undefined;
      globalListenersAttached = false;
    }
  };
};

export const getBoundsSnapshot = (): Bounds => bounds;

export const getServerBoundsSnapshot = (): Bounds => hookInitValues;

/** Resets module state between unit tests. */
export const resetBoundsStoreForTests = (): void => {
  removeGlobalListeners?.();
  removeGlobalListeners = undefined;
  bounds = getWindowBounds();
  listeners.clear();
  globalListenersAttached = false;
  virtualKeyboardHeight = 0;
};
